/**
 * Builds the in-app `/login` route URL, carrying the current in-application path
 * as the `originalDestination` query parameter so the person can be returned to
 * it after a successful login.
 *
 * Now that `/login` is an in-app SPA route served from the same Tenant subdomain
 * (not a separate cross-origin hosted UI), this returns an in-app RELATIVE path
 * — `/login?originalDestination={encodeURIComponent(currentPath)}` — with no
 * scheme or host. Callers navigate to it in-app (React Router `<Navigate>` /
 * same-origin `location.assign`) rather than performing a cross-origin
 * full-page redirect. The `currentPath` is URL-encoded so it round-trips
 * exactly once decoded.
 *
 * @param {string} currentPath the in-application path (e.g. `/analyze?id=5`) to return to after login
 * @returns {string} the in-app relative `/login` URL
 */
function buildLoginRedirectUrl(currentPath) {
  return `/login?originalDestination=${encodeURIComponent(currentPath)}`;
}

/**
 * Validates that a candidate Original_Destination_URL is a same-origin, relative
 * path within the Tenant's own Insights subdomain, before it is ever used as a
 * redirect target (both on the frontend before redirecting to Amplify_Login_UI,
 * and mirrored server-side in Cognito_Login_Lambda — defense in depth).
 *
 * Accepts a value if and only if it is a bare, relative path: it must start with
 * a single `/`, must not start with a `//` authority prefix (protocol-relative
 * URL), must not contain a `://` scheme separator, and must not embed any scheme
 * (e.g. `javascript:`). Every absolute URL, protocol-relative URL, or
 * cross-origin/embedded-scheme value is rejected; a rejected value is treated as
 * though no Original_Destination_URL were present.
 *
 * @param {string} value the candidate Original_Destination_URL to validate
 * @param {string} [tenantHost] the Tenant's login host (accepted for interface
 *   symmetry with buildLoginRedirectUrl; a same-origin relative path carries no
 *   host of its own to compare against)
 * @returns {boolean} true iff the value is a same-origin relative path
 */
// eslint-disable-next-line no-unused-vars
function isSameOriginPath(value, tenantHost) {
  if (typeof value !== "string" || value.length === 0) {
    return false;
  }
  // Must be a bare, relative path rooted at a single "/".
  if (value[0] !== "/") {
    return false;
  }
  // Reject protocol-relative URLs like "//evil.com".
  if (value[1] === "/") {
    return false;
  }
  // Reject backslashes anywhere. Browsers normalize "\" to "/" in special
  // (http/https) URLs, so "/\evil.example" is navigated as the protocol-relative
  // "//evil.example" (an external authority) and would slip past the "//" check
  // above.
  if (value.includes("\\")) {
    return false;
  }
  // Reject an embedded scheme, e.g. "https://evil.com/x" or "javascript:alert(1)".
  // A scheme-forming ":" can only appear in the first path segment — the part
  // after the leading "/" and before the next "/", "?", or "#". A ":" inside a
  // later path segment, the query, or the fragment (e.g. "/search?t=12:30" or
  // "/item#a:b") cannot form a scheme, so those valid same-origin paths are
  // allowed through. We therefore isolate the first path segment (starting at
  // index 1, since value[0] is always "/" here) and reject only a ":" within it.
  const firstSegmentEnd = value.slice(1).search(/[/?#]/);
  const firstSegment =
    firstSegmentEnd === -1
      ? value.slice(1)
      : value.slice(1, firstSegmentEnd + 1);
  if (firstSegment.includes(":")) {
    return false;
  }
  return true;
}

/**
 * Resolves the Tenant for the Amplify_Login_UI from the subdomain it is served
 * from, using the same convention the rest of the SPA already uses
 * (`window.location.hostname.split(".")[0]`, cf. App.js / URIUtils.js). For
 * example, `srjc.elumeninsights.com` resolves to Tenant `srjc`.
 *
 * The Cognito_User_Pool is shared across all Tenants within a Stage, so Tenant
 * is never inferred from the pool or app client; it is resolved here from the
 * subdomain and carried explicitly to the backend (as clientMetadata.tenant on
 * sign-up, and as a request parameter on the login callback).
 *
 * @param {string} [hostname=window.location.hostname] the host to resolve from
 *   (defaults to the current window's hostname; injectable for testing)
 * @returns {string} the resolved Tenant (the leftmost subdomain label)
 */
function resolveTenantFromSubdomain(
  hostname = typeof window !== "undefined" ? window.location.hostname : ""
) {
  return String(hostname).split(".")[0];
}

/**
 * Builds the `clientMetadata` object the Amplify_Login_UI sign-up form must
 * carry so the Stage's shared Cognito_User_Pool passes the Tenant through to
 * Cognito_PreSignUp_Lambda (per the PreSignUp event's
 * `request.clientMetadata.tenant` shape).
 *
 * The Tenant is resolved from the subdomain the Amplify_Login_UI is served from
 * (see resolveTenantFromSubdomain), never from the shared pool/app client. This
 * is passed as the `clientMetadata` option to Amplify's sign-up call so that
 * Cognito_PreSignUp_Lambda can perform its Tenant-scoped, provisioning-closed
 * lookup.
 *
 * @param {string} [hostname=window.location.hostname] the host to resolve the
 *   Tenant from (injectable for testing)
 * @returns {{ tenant: string }} the clientMetadata carried on sign-up
 */
function buildSignUpClientMetadata(
  hostname = typeof window !== "undefined" ? window.location.hostname : ""
) {
  return { tenant: resolveTenantFromSubdomain(hostname) };
}

export {
  buildLoginRedirectUrl,
  isSameOriginPath,
  resolveTenantFromSubdomain,
  buildSignUpClientMetadata,
};
